<template>
    <v-menu v-model="organizationMenu" offset-x nudge-top="8" :close-on-content-click="false">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingSpaces || fetchingOrgs"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeOrgOid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-domain' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingOrgs">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ organizationName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="organizationActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToOrgGeneralSettings()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="isOrgManager" @click="goToOrgUsers()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-account-group-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Users</v-list-item-title>
                                    </v-list-item>
                                    <OrganizationInvite v-if="isOrgManager">
                                        <template v-slot:activator>
                                            <v-list-item @click="organizationActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </OrganizationInvite>
                                    <v-divider class="my-2"></v-divider>
                                    <EntityLeaveDialog entity-type="organization">
                                        <template v-slot:activator>
                                            <v-list-item @click="organizationActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-logout</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Leave organization</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityLeaveDialog>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ organizationName }}</span>
            </v-tooltip>
        </template>

        <ContextSelectorList level="organization" :open="organizationMenu" :selected-id="activeOrgOid" @select="fetchOrgSpaces($event.long_id)" />
    </v-menu>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'
import ContextSelectorList from '@/components/contextSelectors/ContextSelectorList.vue'

export default {
    name: 'OrganizationMenu',
    components: {
        ContextSelectorList,
        OrganizationInvite,
        EntityLeaveDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            organizationMenu: false,
            organizationActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState(['userOrgs', 'fetchingOrgs', 'currentOrgData']),
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapGetters('orgStore', ['orgLongName', 'isOrgManager']),
        organizationName() {
            if (this.$route.params.oid !== undefined) {
                return this.orgLongName
            }
            return this.currentOrgData?.long_id || this.orgNames[0]
        },
        orgNames() {
            return this.userOrgs
                .map(org => org.long_id)
                .sort(function (a, b) {
                    return a.toLowerCase().localeCompare(b.toLowerCase())
                })
        },
        activeOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        }
    },
    methods: {
        goToOrgGeneralSettings() {
            this.organizationActionsMenu = false
            if (!this.activeOrgOid) {
                return
            }
            this.$router.push({
                name: 'config-org-general',
                params: { oid: this.activeOrgOid }
            })
        },
        goToOrgUsers() {
            this.organizationActionsMenu = false
            if (!this.activeOrgOid) {
                return
            }
            this.$router.push({
                name: 'config-org-users',
                params: { oid: this.activeOrgOid }
            })
        },
        fetchOrgSpaces(orgName) {
            this.organizationMenu = false
            const org = this.userOrgs.find(item => item.long_id === orgName)
            if (!org) {
                return
            }

            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({
                    name: 'config-org-general',
                    params: { oid: org.oid }
                })
                return
            }

            this.$router.push({ name: 'home-dashboard', params: { oid: org.oid } })
        }
    }
}
</script>
